import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import { AppContext } from "App";
import { ORIGIN_COUNTRIES } from "AppSettings";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { FileSelector, Input, Select, Text } from "components/base/Form";
import Page from "components/base/Page";
import { Alert } from "components/base/Toast";
import LabTestReportsSelector from "components/shared/LabTestReportsSelector";
import MultiProductSelector from "components/shared/MultiProductSelector";
import useFileUploader from "hooks/useFileUploader";
import { useModals } from "ModalProvider";
import moment from "moment";
import ImagesView from "pages/products/gears/ImagesView";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EDIT_CERTIFICATE } from "utils/permissions";
import { getOriginalFileName } from "utils/string";
import { TEST_REPORT_ISSUERS } from "../testReports/consts";
import GenerateCpcPdfView from "./GenerateCpcPdfView";
import GenerateDocPdfView from "./GenerateDocPdfView";
import {
  CREATE_CERTIFICATE,
  DELETE_CERTIFICATE,
  FETCH_CERTIFICATES,
  FETCH_PRODUCTS_FOR_CERTIFICATE_IMAGES,
  UPDATE_CERTIFICATE,
} from "./graphql";
import ImageUrlsSelector from "./ImageUrlsSelector";

const CERTIFICATE_TYPES = ["DOC", "CPC", "GMARK", "CCC"];
const AUTO_ISSUER = "Waboba";
const DEFAULT_CPC_INFO = {
  sampleDescription: "",
  manufacturer: "Waboba AB",
  styleNos: "",
  placeOfManufacturer: "",
  countryOfOrigin: "CHINA",
  dateOfManufacturer: "",
  placeOfTesting: "",
  testReportNumber: "",
  dateOfTesting: "",
};
const DEFAULT_FORM = {
  isActive: true,
  certificateType: "DOC",
  country: ORIGIN_COUNTRIES[0]?.code || "",
  title: "",
  issuedDate: moment().format("YYYY-MM-DD"),
  certificateNumber: "",
  issuer: AUTO_ISSUER,
  validityMonths: "24",
  validUntil: "",
  qrCodeExpireDate: "",
  cpcInfo: DEFAULT_CPC_INFO,
};

const CertificateFormPage = ({
  mode = "create",
  id = null,
  initialValues = {},
  initialProducts = [],
  initialTestReports = [],
  initialFiles = [],
  initialImageUrls = [],
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = mode === "edit" && !!id;
  const { hasPermission } = useContext(AppContext);
  const canEditCertificate = hasPermission(EDIT_CERTIFICATE);
  const [form, setForm] = useState({
    ...DEFAULT_FORM,
    ...initialValues,
    cpcInfo: { ...DEFAULT_CPC_INFO, ...(initialValues.cpcInfo || {}) },
  });
  const [selectedProducts, setSelectedProducts] = useState(initialProducts);
  const [selectedTestReports, setSelectedTestReports] =
    useState(initialTestReports);
  const [files, setFiles] = useState(initialFiles);
  const [certificateImageUrls, setCertificateImageUrls] =
    useState(initialImageUrls);
  const { isUploading, handleFileUpload } = useFileUploader(
    handleUploadedFiles,
    "AWS",
  );

  const [createCertificate, createCertificateRes] = useMutation(
    CREATE_CERTIFICATE,
    {
      onCompleted(res) {
        Alert("success", "Certificate created.");
        const newId = res?.createCertificate?.certificate?.id;
        if (newId) {
          navigate(`/certificates/${newId}`);
        }
      },
      onError(error) {
        Alert("error", parseError(error));
      },
      refetchQueries: [{ query: FETCH_CERTIFICATES, variables: { offset: 0 } }],
      awaitRefetchQueries: true,
    },
  );
  const [updateCertificate, updateCertificateRes] = useMutation(
    UPDATE_CERTIFICATE,
    {
      onCompleted() {
        Alert("success", "Certificate updated.");
      },
      onError(error) {
        Alert("error", parseError(error));
      },
      refetchQueries: [{ query: FETCH_CERTIFICATES, variables: { offset: 0 } }],
      awaitRefetchQueries: true,
    },
  );
  const [deleteCertificate, deleteCertificateRes] = useMutation(
    DELETE_CERTIFICATE,
    {
      onCompleted() {
        Alert("success", "Certificate deleted.");
        navigate(`/certificates?${searchParams.toString()}`);
      },
      onError(error) {
        Alert("error", parseError(error));
      },
      refetchQueries: [{ query: FETCH_CERTIFICATES, variables: { offset: 0 } }],
      awaitRefetchQueries: true,
    },
  );

  const modal = useModals();
  const generateCertificateModel = useModals();

  const selectedProductIds = selectedProducts.map((product) => product.id);
  const defaultPlaceOfTesting =
    TEST_REPORT_ISSUERS[selectedTestReports[0]?.issuer] || "";
  const { data: productImagesData } = useQuery(
    FETCH_PRODUCTS_FOR_CERTIFICATE_IMAGES,
    {
      variables: { ids: selectedProductIds },
      skip: selectedProductIds.length === 0,
      fetchPolicy: "cache-first",
    },
  );

  const availableProductImageUrls = [
    ...new Set(
      (productImagesData?.products?.results || [])
        .flatMap((product) => product?.images || [])
        .map((image) => image?.url)
        .filter(Boolean),
    ),
  ];

  function onChange(key, value) {
    setForm((prev) => {
      if (key === "certificateType" && (value === "DOC" || value === "CPC")) {
        return { ...prev, certificateType: value, issuer: AUTO_ISSUER };
      }
      return { ...prev, [key]: value };
    });
  }

  function onChangeCpcInfo(key, value) {
    setForm((prev) => ({
      ...prev,
      cpcInfo: {
        ...(prev.cpcInfo || {}),
        [key]: value,
      },
    }));
  }

  function removeProduct(productId) {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  }

  function removeTestReport(testReportId) {
    setSelectedTestReports((prev) => prev.filter((r) => r.id !== testReportId));
  }

  function handleUploadedFiles(urls) {
    setFiles((prev) => [...prev, ...urls]);
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function certificateImagesChangeHandler(values) {
    setCertificateImageUrls(values?.imageUrls || []);
  }

  function chooseProductImagesHandler() {
    const initialSelectedUrls = certificateImageUrls.filter((url) =>
      availableProductImageUrls.includes(url),
    );

    modal.present({
      title: "Choose Product Images",
      center: true,
      children: (
        <ImageUrlsSelector
          availableUrls={availableProductImageUrls}
          initialSelectedUrls={initialSelectedUrls}
          onDone={didChooseProductImagesHandler}
        />
      ),
    });
  }

  function didChooseProductImagesHandler(selectedProductImageUrls) {
    modal.hide();
    setCertificateImageUrls((prev) => {
      const customImageUrls = prev.filter(
        (url) => !availableProductImageUrls.includes(url),
      );
      return [...customImageUrls, ...selectedProductImageUrls];
    });
  }

  function chooseProductHandler() {
    modal.present({
      title: "Choose Products",
      center: true,
      children: (
        <MultiProductSelector
          defaultSelectedProducts={selectedProducts}
          onDone={didChooseProductsHandler}
        />
      ),
    });
  }

  function didChooseProductsHandler(products) {
    modal.hide();
    setSelectedProducts(products);
  }

  function chooseTestReportHandler() {
    modal.present({
      title: "Choose Test Reports",
      center: false,
      children: (
        <LabTestReportsSelector
          defaultSelectedTestReports={selectedTestReports}
          onDone={(testReports) => {
            modal.hide();
            setSelectedTestReports(testReports);
            setForm((prev) => ({
              ...prev,
              cpcInfo: {
                ...(prev.cpcInfo || {}),
                placeOfTesting:
                  prev.cpcInfo?.placeOfTesting ||
                  TEST_REPORT_ISSUERS[testReports[0]?.issuer] ||
                  "",
              },
            }));
          }}
        />
      ),
    });
  }

  function generateCertificateHandler() {
    const onCompleted = (generatedFileUrl) => {
      generateCertificateModel.hide();
      if (generatedFileUrl) {
        handleUploadedFiles([generatedFileUrl]);
      }
    };

    if (form.certificateType === "DOC") {
      const docReports = selectedTestReports.flatMap((report) => {
        const standards = report.standards || [];

        if (standards.length === 0) {
          return [
            {
              refNo: report.title || "",
              notifiedBody: report.issuer || "",
              documentNo: report.reportNumber || "",
              title: report.title || "",
              editionDate: report.issuedDate || "",
            },
          ];
        }

        return standards.map((standard) => ({
          refNo: standard.name || "",
          notifiedBody: report.issuer || "",
          documentNo: report.reportNumber || "",
          title: standard.name || "",
          editionDate: report.issuedDate || "",
        }));
      });
      const docDefaultValues = {
        imageUrls: certificateImageUrls,
        docDirectives: "2009/48/EC Toys safety",
        docName: form.title,
        docModelNumber: selectedProducts.map((p) => p.number).join(", "),
        docSignDate: form.issuedDate || "",
        docReports,
      };

      generateCertificateModel.present({
        title: "Generate Certificate",
        fullscreen: true,
        children: (
          <GenerateDocPdfView
            defaultValues={docDefaultValues}
            onCompleted={onCompleted}
          />
        ),
      });
      return;
    }

    const cpcInfo = form.cpcInfo || {};
    const cpcDefaultValues = {
      certificateNumber: form.certificateNumber,
      imageUrls: certificateImageUrls,
      sampleDescription: cpcInfo.sampleDescription || form.title,
      manufacturer: cpcInfo.manufacturer || "Waboba AB",
      styleNos:
        cpcInfo.styleNos || selectedProducts.map((p) => p.number).join(", "),
      placeOfManufacturer: cpcInfo.placeOfManufacturer || "CHINA",
      countryOfOrigin: cpcInfo.countryOfOrigin || "CHINA",
      dateOfManufacturer: cpcInfo.dateOfManufacturer || "",
      placeOfTesting: cpcInfo.placeOfTesting || defaultPlaceOfTesting,
      testReportNumber:
        cpcInfo.testReportNumber ||
        selectedTestReports.map((r) => r.reportNumber).join(", "),
      dateOfTesting: selectedTestReports
        .filter((r) => r.issuedDate)
        .map((r) => r.issuedDate)
        .join(", "),
    };

    generateCertificateModel.present({
      title: "Generate Certificate",
      fullscreen: true,
      children: (
        <GenerateCpcPdfView
          type={form.certificateType}
          defaultValues={cpcDefaultValues}
          onCompleted={onCompleted}
        />
      ),
    });
  }

  function saveHandler() {
    const cpcInfo = form.cpcInfo || {};
    const normalizedTitle =
      form.title.trim() ||
      (form.certificateType === "CPC"
        ? (cpcInfo.sampleDescription || "").trim()
        : "");
    const normalizedIssuer =
      form.issuer.trim() ||
      (form.certificateType === "CPC"
        ? (cpcInfo.manufacturer || "").trim()
        : "");

    if (!normalizedTitle) {
      Alert("error", "Please enter title.");
      return;
    }
    if (!form.issuedDate) {
      Alert("error", "Please select issued date.");
      return;
    }
    if (!form.country) {
      Alert("error", "Please select country.");
      return;
    }
    if (isUploading) {
      Alert("error", "Please wait for file upload to complete.");
      return;
    }

    const testReportIds = selectedTestReports.map((report) => report.id);
    const fileUrls = files.filter(Boolean);
    const imageUrls = certificateImageUrls.filter(Boolean);
    const validityMonths = Number(form.validityMonths);

    const variables = {
      certificateType: form.certificateType,
      isActive: !!form.isActive,
      title: normalizedTitle,
      issuedDate: form.issuedDate,
      certificateNumber: form.certificateNumber.trim() || undefined,
      issuer: normalizedIssuer || undefined,
      country: form.country || undefined,
      validityMonths:
        form.validityMonths === "" || Number.isNaN(validityMonths)
          ? undefined
          : validityMonths,
      validUntil: form.validUntil || undefined,
      qrCodeExpireDate: form.qrCodeExpireDate || undefined,
      productIds:
        selectedProducts.length > 0
          ? selectedProducts.map((p) => p.id)
          : undefined,
      testReportIds: testReportIds.length > 0 ? testReportIds : undefined,
      imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
      fileUrls: fileUrls.length > 0 ? fileUrls : undefined,
    };

    if (isEditMode) {
      updateCertificate({ variables: { id, ...variables } });
      return;
    }
    createCertificate({ variables });
  }

  function deleteHandler() {
    if (!isEditMode) return;
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this certificate?",
    );
    if (!shouldDelete) return;
    deleteCertificate({ variables: { id } });
  }

  if (!canEditCertificate) {
    return (
      <Page
        title={isEditMode ? "Edit Certificate" : "New Certificate"}
        backTo="./.."
      >
        <div className="p-6">
          <div className="card text-sm text-orange-600">
            You don&apos;t have permission to edit certificates.
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page
      title={isEditMode ? "Edit Certificate" : "New Certificate"}
      backTo="./.."
      rightButtons={
        <Button
          primary
          loading={createCertificateRes.loading || updateCertificateRes.loading}
          onClick={saveHandler}
        >
          {isEditMode ? "Save Certificate" : "Create Certificate"}
        </Button>
      }
    >
      <div className="p-6 grid grid-cols-12 gap-6">
        <div className="col-span-8 space-y-6">
          {createCertificateRes.error || updateCertificateRes.error ? (
            <div className="card">
              <Errors
                error={createCertificateRes.error || updateCertificateRes.error}
              />
            </div>
          ) : null}

          <div className="card space-y-4">
            <h5>Certificate Information</h5>

            <div className="-mx-2">
              <table>
                <tbody>
                  <tr>
                    <td className="w-44">
                      <label>Country *</label>
                    </td>
                    <td>
                      <Select
                        className="w-full"
                        value={form.country}
                        onChange={(e) => onChange("country", e.target.value)}
                      >
                        {ORIGIN_COUNTRIES.map((i, index) => (
                          <option key={index} value={i.code}>
                            {i.name} {i.emoji}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-44">
                      <label>Certificate Type *</label>
                    </td>
                    <td>
                      <Select
                        className="w-full"
                        value={form.certificateType}
                        onChange={(e) =>
                          onChange("certificateType", e.target.value)
                        }
                      >
                        {CERTIFICATE_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Certificate Number</label>
                    </td>
                    <td>
                      <Input
                        className="w-full"
                        value={form.certificateNumber}
                        onChange={(e) =>
                          onChange("certificateNumber", e.target.value)
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Title *</label>
                    </td>
                    <td>
                      <Input
                        className="w-full"
                        value={form.title}
                        onChange={(e) => onChange("title", e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Issuer</label>
                    </td>
                    <td>
                      <Input
                        className="w-full"
                        value={form.issuer}
                        onChange={(e) => onChange("issuer", e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <label>Issued Date *</label>
                    </td>
                    <td>
                      <Input
                        className="w-full"
                        type="date"
                        value={form.issuedDate}
                        onChange={(e) => onChange("issuedDate", e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="align-baseline">
                      <label>Valid Until</label>
                    </td>
                    <td className="align-baseline">
                      <Input
                        className="w-full"
                        type="date"
                        value={form.validUntil}
                        onChange={(e) => onChange("validUntil", e.target.value)}
                      />
                      <div className="opacity-70 text-xs p-1">
                        Default Valid Until will be calculated based on Issued
                        Date and {form.validityMonths} Months if Valid Until is
                        not provided.
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="align-baseline">
                      <label>QR Code Expire Date</label>
                    </td>
                    <td className="align-baseline">
                      <Input
                        className="w-full"
                        type="date"
                        value={form.qrCodeExpireDate}
                        onChange={(e) =>
                          onChange("qrCodeExpireDate", e.target.value)
                        }
                      />
                      <div className="opacity-70 text-xs p-1">
                        For GMARK Certificates.
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="card space-y-4">
            <h5>Test Reports</h5>
            {selectedTestReports.length > 0 ? (
              <div className="-mx-2">
                <table className="with-line">
                  <tbody>
                    {selectedTestReports.map((report) => (
                      <tr key={report.id}>
                        <td>
                          <div className="flex justify-between items-center">
                            <div>
                              {report.reportNumber && (
                                <div className=" opacity-70">
                                  {report.reportNumber}
                                </div>
                              )}
                              <div>{report.title || "-"}</div>
                            </div>
                            <Button
                              trash
                              onClick={() => removeTestReport(report.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div>
              <Button onClick={chooseTestReportHandler}>
                + Choose Test Report
              </Button>
            </div>
          </div>

          {form.certificateType === "CPC" && (
            <div className="card space-y-4 hidden">
              <h5>CPC Info</h5>
              <div className="-mx-2">
                <table>
                  <tbody>
                    <tr>
                      <td className="w-52">
                        <label>Sample Description</label>
                      </td>
                      <td colSpan={3}>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.sampleDescription}
                          onChange={(e) =>
                            onChangeCpcInfo("sampleDescription", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <label>Manufacturer</label>
                      </td>
                      <td>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.manufacturer}
                          onChange={(e) =>
                            onChangeCpcInfo("manufacturer", e.target.value)
                          }
                        />
                      </td>
                      <td className="w-44">
                        <label>Style No(s)</label>
                      </td>
                      <td>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.styleNos}
                          onChange={(e) =>
                            onChangeCpcInfo("styleNos", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <label>Place of Manufacturer</label>
                      </td>
                      <td>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.placeOfManufacturer}
                          onChange={(e) =>
                            onChangeCpcInfo(
                              "placeOfManufacturer",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td>
                        <label>Country of Origin</label>
                      </td>
                      <td>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.countryOfOrigin}
                          onChange={(e) =>
                            onChangeCpcInfo("countryOfOrigin", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <label>Date of Manufacturer</label>
                      </td>
                      <td>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.dateOfManufacturer}
                          onChange={(e) =>
                            onChangeCpcInfo(
                              "dateOfManufacturer",
                              e.target.value,
                            )
                          }
                        />
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                    <tr>
                      <td className="align-top pt-2">
                        <label>Place of Testing</label>
                      </td>
                      <td colSpan={3}>
                        <Text
                          className="w-full"
                          value={form.cpcInfo.placeOfTesting}
                          onChange={(e) =>
                            onChangeCpcInfo("placeOfTesting", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <label>Test Report Number</label>
                      </td>
                      <td>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.testReportNumber}
                          onChange={(e) =>
                            onChangeCpcInfo("testReportNumber", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <label>Date of Testing</label>
                      </td>
                      <td>
                        <Input
                          className="w-full"
                          value={form.cpcInfo.dateOfTesting}
                          onChange={(e) =>
                            onChangeCpcInfo("dateOfTesting", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card space-y-4">
            <h5>Certificate Files</h5>

            {files.length > 0 ? (
              <div className="space-y-2">
                {files.map((url, index) => (
                  <div
                    key={`${url}-${index}`}
                    className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 py-2"
                  >
                    <div className="flex-1">
                      <a
                        href={url}
                        className="link"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {getOriginalFileName(url)}
                      </a>
                    </div>
                    <Button trash onClick={() => removeFile(index)} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm opacity-60">No files uploaded.</div>
            )}

            <div className="flex space-x-4">
              <FileSelector
                title="+ Add Files"
                uploading={isUploading}
                onChange={(e) => {
                  handleFileUpload([...e.target.files]);
                  e.target.value = null;
                }}
              />

              {form.certificateType === "CPC" ||
              form.certificateType === "DOC" ? (
                <Button onClick={generateCertificateHandler}>
                  Generate Certificate
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="col-span-4 space-y-6">
          <div className="card space-y-4">
            <h5>Status</h5>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.isActive}
                onChange={(e) => onChange("isActive", e.target.checked)}
              />
              <span>Is Active</span>
            </label>
          </div>

          <div className="card space-y-4">
            <h5>Products</h5>
            {selectedProducts.length > 0 ? (
              <div className="-mx-2">
                <table className="with-line">
                  <tbody>
                    {selectedProducts.map((product) => (
                      <tr key={product.id}>
                        <td>
                          <div className="flex justify-between items-center">
                            <div>
                              [{product.number}] {product.name}
                              {product.version ? ` | v${product.version}` : ""}
                            </div>
                            <Button
                              trash
                              onClick={() => removeProduct(product.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div>
              <Button onClick={chooseProductHandler}>+ Choose Products</Button>
            </div>
          </div>

          <ImagesView
            name="Certificate Images"
            images={certificateImageUrls.map((url, index) => ({
              id: `${url}-${index}`,
              url,
            }))}
            onChange={certificateImagesChangeHandler}
            extraImageSelector={
              <Button
                onClick={chooseProductImagesHandler}
                disabled={availableProductImageUrls.length === 0}
              >
                + Choose Product Images
              </Button>
            }
          />

          <div className="flex justify-end">
            {isEditMode ? (
              <Button
                danger
                loading={deleteCertificateRes.loading}
                onClick={deleteHandler}
              >
                Delete Certificate
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default CertificateFormPage;
